import {
    merge,
    isEqual,
    sortBy,
    orderBy as lodashOrderBy,
    uniqBy,
    isObject as lodashIsObject,
    debounce as lodashDebounce,
    throttle as lodashThrottle,
    pick as lodashPick,
    cloneDeep,
} from 'lodash';

export function mergeDeep <T extends Record<string, any>> (target: T, ...sources: Partial<T>[]): T {
    return merge(target, ...sources);
}

export function deepEqual (obj1: any, obj2: any): boolean {
    return isEqual(obj1, obj2);
}

export function sortByKeys <T> (array: T[], keys: (keyof T | ((item: T) => any))[]): T[] {
    return sortBy(array, keys);
}

export function orderBy <T> (
    array: T[],
    keys: (keyof T | ((item: T) => any))[],
    orders: ('asc' | 'desc')[] = [],
): T[] {
    return lodashOrderBy(array, keys, orders);
}

export function uniqueBy <T> (array: T[], key: keyof T | ((item: T) => any)): T[] {
    return uniqBy(array, key);
}

export function isObject (value: any): value is object {
    return lodashIsObject(value);
}

export function debounce <T extends (...args: any[]) => any> (
    func: T,
    wait: number,
    immediate = false,
): ((...args: Parameters<T>) => ReturnType<T> | undefined) & {
    cancel: () => void;
    flush: () => ReturnType<T> | undefined;
} {
    return lodashDebounce(func, wait, { leading: immediate, trailing: !immediate }) as any;
}

export function throttle <T extends (...args: any[]) => any> (
    func: T,
    wait: number,
): (...args: Parameters<T>) => void {
    return lodashThrottle(func, wait, { trailing: false }) as any;
}

export function pick <T extends object, K extends keyof T> (obj: T, keys: K[]): Pick<T, K> {
    return lodashPick(obj, keys);
}

export function safeClone <T> (obj: T): T {
    return cloneDeep(obj);
}

/**
 * Creates an object with the given key-value pair only if the value is not undefined.
 * Useful for building objects with optional properties when exactOptionalPropertyTypes is enabled.
 *
 * @example
 * const params = {
 *     ...propIfDefined('group_id', domainGroupId),
 *     page: 1,
 * };
 */
export function propIfDefined<K extends string, V> (
    key: K,
    value: V | undefined,
): V extends undefined ? Record<string, never> : { [P in K]: V } {
    return (value !== undefined ? { [key]: value } : {}) as V extends undefined ? Record<string, never> : { [P in K]: V };
}
